import { useCallback, useEffect, useState } from "react";
import { APP_VERSION } from "../version.ts";

/** How often to ask the server what is deployed. */
const POLL_MS = 15 * 60_000;

/**
 * Tells you whether this tab is running the deployed build.
 *
 * The build id is baked into the bundle and also written to `dist/version.json`
 * at build time, so comparing the two answers the question directly rather than
 * inferring it from cache headers. It matters here because the app is a PWA:
 * an installed copy can sit open for days on code that has since been replaced.
 */
export function useVersion() {
  const [latest, setLatest] = useState<string | null>(null);

  const check = useCallback(async () => {
    // Nothing is deployed in dev, and the dev bundle has no build id.
    if (import.meta.env.DEV) return;
    try {
      const res = await fetch("/version.json", { cache: "no-store" });
      if (!res.ok) return;
      const body = (await res.json()) as { version?: unknown };
      if (typeof body.version === "string") setLatest(body.version);
    } catch {
      // Offline, or the server is mid-deploy. Try again on the next tick.
    }
  }, []);

  useEffect(() => {
    void check();
    const timer = setInterval(check, POLL_MS);
    // Coming back to a long-open tab is exactly when it is most likely stale.
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [check]);

  return {
    current: APP_VERSION,
    latest,
    /** A newer build is deployed than the one this tab loaded. */
    stale: latest !== null && latest !== APP_VERSION,
    check,
  };
}

/**
 * Reloads onto the deployed build: clears the cached app shell and tells any
 * waiting service worker to take over, so the next load is not served the old
 * bundle from cache.
 */
export async function reloadToLatest() {
  try {
    const keys = await caches.keys();
    // Media is content-addressed and huge; only the shell can go stale.
    await Promise.all(keys.filter((k) => k.startsWith("t24-shell")).map((k) => caches.delete(k)));
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
    reg?.waiting?.postMessage("skipWaiting");
  } catch {
    // Best effort — the reload below is what actually matters.
  }
  location.reload();
}
